import { useState } from 'react'
import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import { defaultConfig } from './index'

const SOURCE_OPTIONS = [
  { value: 'HL2', label: 'HL2' },
  { value: 'HLC3', label: 'HLC3' },
  { value: 'OHLC4', label: 'OHLC4' },
]

const STYLE_OPTIONS = [
  { value: 'solid', label: 'Sólido' },
  { value: 'dotted', label: 'Puntos' },
  { value: 'dashed', label: 'Guiones' },
  { value: 'largeDashed', label: 'Largo' },
  { value: 'sparseDotted', label: 'Ralo' },
]

const QUANTUM_STYLE_KEYS = [
  'color',
  'lineWidth',
  'lineStyle',
  'bandColor',
  'bandLineWidth',
  'bandLineStyle',
  'bandFillOpacity',
  'showLabel',
]

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

export default function VwapPanel({ config, onChange, onClose }) {
  const cfg = config || {}

  const update = (key, value) => {
    onChange({ ...cfg, [key]: value })
  }

  const resetStyle = () => {
    const patch = {}
    for (const k of QUANTUM_STYLE_KEYS) patch[k] = defaultConfig[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración VWAP" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <Section title="Lógica" sectionKey="logic" config={cfg} onChange={onChange}>
          <SliderField
            label="Barras de ventana"
            value={cfg.aggBars ?? 30}
            min={5}
            max={200}
            step={5}
            unit=""
            onChange={(v) => update('aggBars', v)}
          />
          <div className="space-y-1">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Fuente de precio</span>
            <div className="grid grid-cols-3 gap-2">
              {SOURCE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => update('priceSource', opt.value)}
                  className={`px-2 py-1.5 text-[11px] font-medium rounded border transition-colors ${
                    cfg.priceSource === opt.value
                      ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                      : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <ToggleField label="Mostrar bandas (1σ, 2σ, 3σ)" value={cfg.showBands ?? true} onChange={(v) => update('showBands', v)} />
        </Section>

        <Section title="Estilo" sectionKey="style" config={cfg} onChange={onChange}>
          <ToggleField label="Mostrar dashboard" value={cfg.showDashboard ?? true} onChange={(v) => update('showDashboard', v)} />
          <ToggleField label="Etiqueta de precio en eje" value={cfg.showPriceLabel ?? cfg.showLabel ?? true} onChange={(v) => update('showPriceLabel', v)} />
          <ColorPickerField label="Color VWAP" value={cfg.color ?? '#B08D57'} onChange={(v) => update('color', v)} />
          <SliderField label="Grosor de línea" value={cfg.lineWidth ?? 1.5} min={1} max={4} step={0.5} unit="px" onChange={(v) => update('lineWidth', v)} />
          <StyleSelect label="Estilo de línea" value={cfg.lineStyle ?? 'solid'} onChange={(v) => update('lineStyle', v)} />

          <ColorPickerField label="Color bandas" value={cfg.bandColor ?? '#B08D57'} onChange={(v) => update('bandColor', v)} />
          <SliderField label="Grosor bandas" value={cfg.bandLineWidth ?? 1} min={0.5} max={3} step={0.5} unit="px" onChange={(v) => update('bandLineWidth', v)} />
          <StyleSelect label="Estilo bandas" value={cfg.bandLineStyle ?? 'dashed'} onChange={(v) => update('bandLineStyle', v)} />

          <SliderField
            label="Opacidad relleno bandas"
            value={cfg.bandFillOpacity ?? 0.10}
            min={0}
            max={0.5}
            step={0.01}
            unit=""
            onChange={(v) => update('bandFillOpacity', v)}
          />
          <ToggleField label="Etiqueta de precio" value={cfg.showLabel ?? true} onChange={(v) => update('showLabel', v)} />

          <button
            onClick={resetStyle}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
          >
            <RotateCcw size={12} />
            Restablecer estilo Quantum
          </button>
        </Section>
      </div>
    </IndicatorConfigModal>
  )
}

function StyleSelect({ label, value, onChange }) {
  return (
    <div className="space-y-1">
      <span className="text-xs text-[var(--ink-soft,#4A5A52)]">{label}</span>
      <div className="grid grid-cols-3 gap-2">
        {STYLE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-2 py-1.5 text-[11px] font-medium rounded border transition-colors ${
              value === opt.value
                ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}
